import {
  DailyAnalytic,
  DataType,
  DensityAccount,
  ErrorType,
  Headers,
  MapType,
  Space,
  SpaceHierarchy,
  TagMap,
} from "../../types";
import {
  accountIds,
  buildError,
  dataGenerator,
  tagsGenerator,
  getTagName,
  getUserAccount,
} from "../../utils";
import { useEffect, useRef, useState } from "react";

import { BugTracker, Analytics } from "../../library";
import Configuration from "../../configuration/general.json";
import axios from "axios";
import moment from "moment-timezone";
import useAuth from "./useAuth";
import { useErrorHandler } from "react-error-boundary";
import useLocalStorage from "../storage/useLocalStorage";
import usePreviousValue from "../helpers/usePreviousValue";
import useReports from "../store/useReports";
import useSettings from "../store/useSettings";
import useSpaces from "../store/useSpaces";
import useTags from "../store/useTags";
import useUser from "../store/useUser";

const useApi = (
  authHeaders: Headers,
  account: DensityAccount = DensityAccount.DEMO
) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [headers, setHeaders] = useState<Headers | null>(null);
  const [bgLoading, setBgLoading] = useState<boolean>(false);

  const { storeSpaces, removeSpaces } = useSpaces();
  const { reports, storeReports, removeReports } = useReports();
  const { refreshTags, removeTags } = useTags();
  const { storeUser } = useUser();
  const { settings, writeSetting } = useSettings();
  const { clear } = useLocalStorage();

  const impersonator = useRef<string | null>(null);
  const stack = useRef<string | undefined>(undefined);

  const { token, logout } = useAuth(authHeaders);
  const previousToken = usePreviousValue(token);
  const previousImpersonator = usePreviousValue(impersonator.current);
  const previousStack = usePreviousValue(stack.current);
  const handleError = useErrorHandler();

  useEffect(() => {
    stack.current = settings?.impersonate;
  }, [settings?.impersonate]);

  useEffect(() => {
    if (!headers) {
      return;
    }
    if (
      impersonator.current &&
      impersonator.current === previousImpersonator &&
      stack.current === previousStack
    ) {
      return;
    }

    if (stack.current !== previousStack) {
      delete headers["X-Impersonate-User"];
    }

    const formatDate = (date: Date) => {
      return moment(date).format("YYYY-MM-DD");
    };

    const getAnalytics = async (spaces: Space[]) => {
      // Separate by unique timezones and fetch data and break up into `space_analytics` variable.

      let space_analytics: MapType = {};
      const days_to_filter_out: number[] = [];

      const timezones = new Set<string>();

      spaces.map((space) => timezones.add(space["time_zone"]));

      for (const timezone of timezones) {
        // Build the spaces string for this timezone
        let spaces_in_this_timezone = spaces.filter(
          (space) => space["time_zone"] == timezone
        );
        let spaces_id_in_this_timezone = spaces_in_this_timezone.map(
          (space) => space["id"]
        );
        let spaces_str = spaces_id_in_this_timezone.join(",");

        // Build the start and end time with proper timezone offset
        // let start_time = moment.tz("2020-10-07T00:00:00.000", timezone);
        // let end_time = moment.tz(
        //   moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS"),
        //   timezone
        // );
        let start_time = moment("2020-10-07");
        let end_time = moment(
          moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS")
        );

        // Get data from API
        try {
          const spacesResponse = await axios({
            method: "GET",
            url: `${
              Configuration.apiBaseCountsUrl
            }/spaces/counts?start_time=${start_time.format(
              "YYYY-MM-DDTHH:mm:ss.SSS"
            )}&end_time=${end_time.format(
              "YYYY-MM-DDTHH:mm:ss.SSS"
            )}&interval=1d&space_list=${spaces_str}&order=asc&page_size=10000`,
            headers: headers,
          });
          Object.keys(spacesResponse?.data.results).map((id) => {
            space_analytics[id] = {};
            let temp_space_analytics: { [key: string]: any } = {};
            let analytics = spacesResponse?.data.results[id];
            const filtered_analytics = analytics
              .map((item: DailyAnalytic) => {
                return {
                  ...item,
                  timestamp: moment(item.timestamp)
                    .tz(timezone)
                    .format("YYYY-MM-DDTHH:mm:ss.SSS"),
                  interval: {
                    ...item.interval,
                    start: moment(item.interval.start)
                      .tz(timezone)
                      .format("YYYY-MM-DDTHH:mm:ss.SSS"),
                    end: moment(item.interval.end)
                      .tz(timezone)
                      .format("YYYY-MM-DDTHH:mm:ss.SSS"),
                  },
                };
              })
              .filter(
                (item: { [x: string]: number }) =>
                  !days_to_filter_out.includes(
                    new Date(item["timestamp"]).getDay()
                  )
              )
              .sort((a: { [key: string]: any }, b: { [key: string]: any }) => {
                return a.timestamp < b.timestamp
                  ? -1
                  : a.timestamp > b.timestamp
                  ? 1
                  : 0;
              });

            temp_space_analytics["daily"] = filtered_analytics;
            temp_space_analytics["weekly"] = {};

            let weekly_buckets: MapType = {};

            filtered_analytics.map((analytic: { [x: string]: any }) => {
              // Monday is 0
              const timestamp = analytic["timestamp"];
              let weekday = new Date(timestamp).getDay();
              weekday = weekday === 0 ? 7 : weekday;
              let monday;

              if (weekday !== 1) {
                var temp = new Date(timestamp);
                monday = new Date(temp.setDate(temp.getDate() - weekday + 1));
              } else {
                monday = new Date(timestamp);
              }
              const formattedMonday = formatDate(monday);
              if (!Object.keys(weekly_buckets).includes(formattedMonday)) {
                weekly_buckets[formattedMonday] = [];
              }
              weekly_buckets[formattedMonday].push(analytic);

              // Now compute the max and max average for each bucket
              Object.keys(weekly_buckets).map((week_key) => {
                const max_count_per_day: number[] = [];
                weekly_buckets[week_key].map((day: MapType) =>
                  max_count_per_day.push(day.interval.analytics.max)
                );
                temp_space_analytics["weekly"][week_key] = {};
                temp_space_analytics["weekly"][week_key]["max"] = Math.max(
                  ...max_count_per_day
                );
                temp_space_analytics["weekly"][week_key]["average_max"] =
                  max_count_per_day.reduce((mean, value) => mean + value) /
                  max_count_per_day.length;
              });
            });
            // Process for DEMO
            //            space_analytics[id] = demoProcess(temp_space_analytics);
            space_analytics[id] = temp_space_analytics;
          });
        } catch (error) {
          handleError(buildError(ErrorType.API_UNAUTHORIZED, error.message));
        }
      }
      return space_analytics;
    };

    const getTags = async () => {
      let nextSpacesUrl = `${Configuration.apiBaseUrl}/spaces/?page=1&page_size=50000"`;

      try {
        let spacesResponse = await axios({
          method: "GET",
          url: nextSpacesUrl,
          headers: headers,
        });
        return spacesResponse.data.results || [];
      } catch (error) {
        handleError(buildError(ErrorType.API_UNAUTHORIZED, error.message));
      }
    };

    const getSpaces = async () => {
      let spacesResponse;
      try {
        spacesResponse = await axios({
          method: "GET",
          url: `${Configuration.apiBaseUrl}/spaces/hierarchy?page=1&page_size=5000`,
          headers: headers,
        });

        return spacesResponse.data || [];
      } catch (error) {
        handleError(buildError(ErrorType.API_UNAUTHORIZED, error.message));
      }
    };

    const getUser = async () => {
      try {
        const response = await axios({
          method: "GET",
          url: `${Configuration.apiBaseUrl}/users/me`,
          headers: headers,
        });

        // Only identify and store user if not impersonating (signed in user)
        if (headers && !headers["X-Impersonate-User"]) {
          Analytics.identify(response.data.id);
          storeUser(response.data);
          Analytics.track("Portfolio Login");
        }

        return response.data;
      } catch (error) {
        handleError(buildError(ErrorType.API_UNAUTHORIZED, error.message));
      }
    };

    const chekIfImpersonate = async () => {
      if (!headers) {
        return false;
      }

      let newHeaders: Headers | null = headers;

      try {
        // Figure out if we need to put impersonator header in
        const response = await axios({
          method: "GET",
          url: `${Configuration.densityApi}/tokens/check`,
          headers: headers,
        });
        if (response.statusText !== "OK") {
          logout();
        }
        const content = response.data;
        if (content.permissions.includes("impersonate")) {
          let targetAccount: DensityAccount = account;
          if (stack.current) {
            targetAccount = <DensityAccount>stack.current;
          }
          impersonator.current = `${accountIds[targetAccount].usr}`;
          newHeaders["X-Impersonate-User"] = impersonator.current;
          setHeaders(newHeaders);
          return true;
        } else {
          return false;
        }
      } catch (error) {
        handleError(buildError(ErrorType.API_UNAUTHORIZED, error.message));
        return false;
      }
    };

    const getData = async () => {
      const sessionUser = await getUser();

      const impersonate = await chekIfImpersonate();

      let targetAccount: DensityAccount = account;
      if (stack.current) {
        targetAccount = <DensityAccount>stack.current;
        writeSetting({
          isImpersonating: true,
        });
      } else {
        if (impersonate) {
          targetAccount = DensityAccount.DEMO;
          writeSetting({
            isImpersonating: true,
          });
        } else {
          targetAccount = getUserAccount(sessionUser?.organization.id);
          writeSetting({
            isImpersonating: false,
          });
        }
      }

      let spaces: SpaceHierarchy[] = await getSpaces();
      let addressLookup: any = {};  // Cache the updated address for a space ID

      // Some spaces will have a null address, we want to inherit the parent address if one exists
      // Recursive function to populate null address with parent address
      let updateAddress = (space: any, parentAddress: any) => {
        if (space.address === null) {
          space.address = parentAddress;
        }
        if ("children" in space) {
          for (let childSpace of space.children) {
            updateAddress(childSpace, space.address);
          }
        }
        addressLookup[space.id] = space.address;
      };

      // Go through all spaces in hierarchy and fill in address with parent address if null
      for (let space of spaces) {
        updateAddress(space, null);
      }

      if (!impersonate && targetAccount === DensityAccount.OTHER) {
        handleError(
          buildError(
            ErrorType.AUTH,
            "You are not authorized to use Portfolio. Please contact Support"
          )
        );
      }
      // Grab the Signal Campus
      let spaceIds: string[] | undefined;
      try {
        spaceIds = accountIds[targetAccount].filter(spaces);
      } catch (error) {
        handleError(buildError(ErrorType.API_UNAUTHORIZED, error.message));
      }

      spaces.filter((space: { id: string }) => {
        spaceIds?.includes(space.id);
      });

      let relevantSpaces: Space[] = await getTags();

      relevantSpaces = relevantSpaces.filter((space: Space) =>
        spaceIds?.includes(space.id)
      );

      // Add local metadata
      // Hack: for Splunk
      relevantSpaces = relevantSpaces.filter(
        (space: Space) => space.name !== "APAC Aggregate"
      );
      relevantSpaces = relevantSpaces.map((space: Space) => {
        space.selected = false;
        space.filtered = false;
        space.address = addressLookup[space.id];
        return space;
      });

      // Anonymize the demo data
      if (targetAccount === DensityAccount.DEMO) {
        relevantSpaces = relevantSpaces.map((space: Space, index: number) => ({
          ...space,
          name: dataGenerator(index),
          address: dataGenerator(index),
          tags: tagsGenerator(index, space.tags.length),
        }));
      }
      storeSpaces(relevantSpaces);

      let tags: Set<string> = new Set();
      for (let space of relevantSpaces) {
        space.tags.map((tag: string) => {
          tags.add(tag);
        });
      }
      let tagsFinal: TagMap = {};
      Array.from(tags).map((tag: string) => {
        const { name, category } = getTagName(tag);
        tagsFinal[tag] = {
          title: name,
          category: category,
          number: 0,
          selected: false,
          disabled: false,
          shared: false,
        };
      });
      for (let id of Object.keys(tagsFinal)) {
        tagsFinal[id].number = relevantSpaces.reduce(
          (accumulator: number, space: Space) =>
            space.tags.find((tag: string) => tag === id)
              ? accumulator + 1
              : accumulator,
          0
        );
      }

      refreshTags(tagsFinal);

      const analytics = await getAnalytics(relevantSpaces);
      return analytics;
    };
    // Load from local storage
    if (stack.current !== previousStack) {
      clear();
      removeSpaces();
      removeReports();
      removeTags();
      setLoading(true);
    } else {
      if (reports) {
        storeReports(reports);
        setBgLoading(true);
      } else {
        setLoading(true);
      }
    }
    // Get new data in the background
    getData()
      .then((_data) => {
        storeReports(_data);
      })
      .catch((error) => {
        console.log(error);
        BugTracker.captureException(error);
      })
      .finally(() => {
        setLoading(false);
        setBgLoading(false);
      });
  }, [headers, settings?.impersonate]);

  useEffect(() => {
    if (token === previousToken) return;

    if (headers && headers["X-Impersonate-User"]) {
      setHeaders({
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Impersonate-User": headers["X-Impersonate-User"],
      });
    } else {
      setHeaders({
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      });
    }
  }, [token]);

  const updateSafeCapacity = async (id: string, safeCapacity: number) => {
    if (settings?.impersonate === DensityAccount.DEMO) {
      return undefined;
    }
    Analytics.track("Portfolio Edit Safe Capacity", { value: safeCapacity });
    return await axios({
      method: "PUT",
      url: `${Configuration.densityApi}/spaces/${id}`,
      headers: headers,
      data: JSON.stringify({ safe_capacity: safeCapacity }),
    });
  };

  const updateCapacity = async (id: string, capacity: number) => {
    if (settings?.impersonate === DensityAccount.DEMO) {
      return undefined;
    }
    Analytics.track(
      settings?.capacity === DataType.LegalCapacity
        ? "Portfolio Edit Legal Capacity"
        : "Portfolio Edit Target Capacity",
      { value: capacity }
    );
    return await axios({
      method: "PUT",
      url: `${Configuration.densityApi}/spaces/${id}`,
      headers: headers,
      data: JSON.stringify(
        settings?.capacity === DataType.LegalCapacity
          ? { capacity: capacity }
          : { target_capacity: capacity }
      ),
    });
  };

  const addTag = async (id: string, tag: string) => {
    if (settings?.impersonate === DensityAccount.DEMO) {
      return undefined;
    }
    Analytics.track("Portfolio Add Tag", { value: tag });
    return await axios({
      method: "POST",
      url: `${Configuration.densityApi}/spaces/${id}/tags/`,
      headers: headers,
      data: JSON.stringify({ tag_name: tag }),
    });
  };

  const removeTag = async (id: string, tag: string) => {
    if (settings?.impersonate === DensityAccount.DEMO) {
      return undefined;
    }
    Analytics.track("Portfolio Delete Tag", { value: tag });
    return await axios({
      method: "DELETE",
      url: `${Configuration.densityApi}/spaces/${id}/tags/${tag}`,
      headers: headers,
    });
  };

  return {
    loading,
    bgLoading,
    updateCapacity,
    updateSafeCapacity,
    addTag,
    removeTag,
  };
};

export default useApi;
